import React from "react";
import styled from "styled-components";
import moment from "moment";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import { colors } from "../../../Shared/colors";
import { StatusMeta } from "./socialLeadsStyles";
import { formatDuration } from "./socialLeadsFormat";

/**
 * Everything one prospect told Meta, in a side panel.
 *
 * The table can only carry name and phone; every other answer — city, address,
 * and whichever qualifying question the form asks — lives here. Answers come
 * from Meta's payload rather than the stored lead, so this works for rows that
 * were never imported and for leads captured before answers were being saved.
 */
export const SocialLeadsAnswersDrawer = ({
  row,
  onClose,
  onImport,
  isImporting,
  slaMinutes = 60,
}) => {
  const open = Boolean(row);
  const answers = row?.answers || [];
  const meta = row ? StatusMeta[row.status] : null;

  // An absent `answers` key and an empty one mean different things: the first
  // says the server never sent answers, the second says the prospect was never
  // asked anything beyond name and phone. Claiming the second when it is really
  // the first tells the user their form is emptier than it is.
  const answersUnavailable = row ? row.answers === undefined : false;

  const copy = (text) => {
    if (navigator?.clipboard && text) navigator.clipboard.writeText(text);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 430 }, maxWidth: "100%" } }}
    >
      {row ? (
        <Panel>
          <Head>
            <div className="titles">
              <h2>{row.name || "Unnamed submission"}</h2>
              {meta ? (
                <Pill $color={meta.color} $bg={meta.bg}>
                  {meta.label}
                </Pill>
              ) : null}
            </div>
            <IconButton onClick={onClose} size="small" aria-label="Close details">
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </Head>

          <Section>
            <Row>
              <span className="k">Phone</span>
              <span className="v">
                {row.phone ? (
                  <>
                    <a href={`tel:${row.phone}`}>{row.phone}</a>
                    <CopyBtn
                      type="button"
                      title="Copy phone number"
                      onClick={() => copy(row.phone)}
                    >
                      <ContentCopyOutlinedIcon />
                    </CopyBtn>
                  </>
                ) : (
                  <em>No usable phone number</em>
                )}
              </span>
            </Row>
            <Row>
              <span className="k">Form</span>
              <span className="v">{row.formName || "—"}</span>
            </Row>
            {row.projectName ? (
              <Row>
                <span className="k">Project</span>
                <span className="v">{row.projectName}</span>
              </Row>
            ) : null}
            <Row>
              <span className="k">Submitted</span>
              <span className="v">
                {row.submittedAt
                  ? `${moment(row.submittedAt).format("DD MMM YYYY, HH:mm")} · ${moment(
                      row.submittedAt
                    ).fromNow()}`
                  : "—"}
              </span>
            </Row>
            <Row>
              <span className="k">Assigned to</span>
              <span className="v">{row.assignedTo?.name || <em>Not yet assigned</em>}</span>
            </Row>
            {row.status === "inCrm" ? (
              <Row>
                <span className="k">First contact</span>
                <span className="v">
                  {row.minutesToFirstContact !== null &&
                  row.minutesToFirstContact !== undefined ? (
                    <>
                      {formatDuration(row.minutesToFirstContact)} after submission
                      {row.firstContactAt ? (
                        <span className="when">
                          ({moment(row.firstContactAt).format("DD MMM, HH:mm")})
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <Waiting $overdue={(row.minutesWaiting ?? 0) > slaMinutes}>
                      Nobody has called yet · {formatDuration(row.minutesWaiting)} so far
                    </Waiting>
                  )}
                </span>
              </Row>
            ) : null}
            {row.leadID ? (
              <Row>
                <span className="k">Lead ID</span>
                <span className="v mono">{row.leadID}</span>
              </Row>
            ) : null}
          </Section>

          {row.error ? <ErrorNote>{row.error}</ErrorNote> : null}

          <SubHead>
            What they told us
            {answers.length ? <span className="n">{answers.length}</span> : null}
          </SubHead>

          {answers.length ? (
            <Answers>
              {answers.map((answer) => (
                <li key={answer.key}>
                  <span className="q">{answer.label}</span>
                  <span className="a">{answer.value}</span>
                </li>
              ))}
            </Answers>
          ) : (
            <NoAnswers>
              <QuizOutlinedIcon />
              {answersUnavailable ? (
                <p>
                  Answers couldn't be loaded for this submission. They are still
                  held on Meta — reload the page, and if they stay missing the
                  server may not be sending them yet.
                </p>
              ) : (
                <p>
                  This form only asked for name and phone number. Add questions
                  to the form on Meta to capture more here.
                </p>
              )}
            </NoAnswers>
          )}

          {row.status !== "inCrm" ? (
            <Foot>
              <ImportBtn
                type="button"
                disabled={isImporting}
                onClick={() => onImport([row.id])}
              >
                {isImporting
                  ? "Importing…"
                  : row.status === "failed"
                  ? "Retry import"
                  : "Import into CRM"}
              </ImportBtn>
            </Foot>
          ) : null}
        </Panel>
      ) : null}
    </Drawer>
  );
};

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${colors.white};
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 18px 16px 14px 20px;
  border-bottom: 1px solid ${colors.inputBorderWhite};

  .titles {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 7px;
    min-width: 0;
  }
  h2 {
    margin: 0;
    font-size: 1.02rem;
    font-weight: 700;
    color: ${colors.text};
    overflow-wrap: anywhere;
  }
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
  color: ${(props) => props.$color};
  background: ${(props) => props.$bg};

  &::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const Section = styled.div`
  padding: 14px 20px 4px 20px;
`;

const Row = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 7px 0;
  font-size: 0.83rem;
  line-height: 1.45;

  .k {
    flex: 0 0 96px;
    color: ${colors.textLight};
    font-weight: 600;
  }
  .v {
    color: ${colors.text};
    min-width: 0;
    overflow-wrap: anywhere;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .v em {
    color: ${colors.textLight};
    font-style: normal;
  }
  .v.mono {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: ${colors.brown};
  }
  .v a {
    color: ${colors.textBlue};
    text-decoration: none;
    font-variant-numeric: tabular-nums;
  }
  .v a:hover {
    text-decoration: underline;
  }
  .v .when {
    color: ${colors.textLight};
    font-size: 0.78rem;
  }
`;

const Waiting = styled.span`
  font-weight: 600;
  color: ${(props) => (props.$overdue ? colors.veryHot : colors.textMed)};
`;

const CopyBtn = styled.button`
  border: none;
  background: none;
  padding: 2px;
  cursor: pointer;
  display: inline-flex;
  color: ${colors.textLight};

  svg {
    font-size: 0.92rem;
  }
  &:hover {
    color: ${colors.brown};
  }
`;

const ErrorNote = styled.div`
  margin: 8px 20px 0 20px;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 0.8rem;
  color: ${colors.veryHot};
  background: rgba(232, 11, 11, 0.07);
  border: 1px solid rgba(232, 11, 11, 0.18);
`;

const SubHead = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 20px 20px 0 20px;
  padding-top: 14px;
  border-top: 1px solid ${colors.inputBorderWhite};
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${colors.textLight};

  .n {
    font-size: 0.68rem;
    font-weight: 700;
    color: ${colors.brown};
    background: ${colors.lightWhite};
    border-radius: 20px;
    padding: 1px 7px;
    letter-spacing: 0;
  }
`;

const Answers = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding: 0 20px 20px 20px;
  overflow-y: auto;
  flex: 1;

  li {
    padding: 9px 0;
    border-bottom: 1px dashed ${colors.inputBorderWhite};
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  li:last-child {
    border-bottom: none;
  }
  .q {
    font-size: 0.72rem;
    color: ${colors.textLight};
    line-height: 1.4;
  }
  .a {
    font-size: 0.88rem;
    font-weight: 600;
    color: ${colors.text};
    overflow-wrap: anywhere;
  }
`;

const NoAnswers = styled.div`
  flex: 1;
  padding: 26px 20px;
  text-align: center;
  color: ${colors.textMed};

  svg {
    font-size: 1.9rem;
    color: ${colors.textLight};
    margin-bottom: 8px;
  }
  p {
    margin: 0 auto;
    font-size: 0.82rem;
    max-width: 34ch;
    line-height: 1.5;
  }
`;

const Foot = styled.div`
  padding: 13px 20px;
  border-top: 1px solid ${colors.inputBorderWhite};
  background: ${colors.lightWhite};
`;

const ImportBtn = styled.button`
  width: 100%;
  font-family: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  padding: 10px 14px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${colors.brown};
  background: ${colors.brown};
  color: ${colors.white};
  transition: opacity 0.18s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
